import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { PlayerAvatar, PhotoUpload } from '../components/ui/Avatar';
import { 
  PageHeader, StatCard, EmptyState, LoadingSkeleton, 
  StatusBadge, WarningBanner, SuccessMessage, InfoBanner,
  ProgressBar
} from '../components/ui/shared';
import { 
  Users, Plus, Edit, MapPin, Phone, Mail, Calendar, 
  User, Shield, AlertCircle, ChevronRight, Home,
  CreditCard, CheckCircle, Clock, FileText, X, Save,
  UserPlus, Trash2, Send, GraduationCap, School
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getDivisionLogo } from '../constants/divisionLogos';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Division display names mapping for registration
// Division display names mapping - Maps age groups and division IDs to display names
const DIVISION_DISPLAY = {
  // Flag Football - Little Chiefs (5-7U)
  '5-6U': 'Flag Football — Little Chiefs (5–7U)',
  '5-7U': 'Flag Football — Little Chiefs (5–7U)',
  '5U': 'Flag Football — Little Chiefs (5–7U)',
  '6U': 'Flag Football — Little Chiefs (5–7U)',
  'div_5_6u': 'Flag Football — Little Chiefs (5–7U)',
  'div_little_chiefs': 'Flag Football — Little Chiefs (5–7U)',
  
  // Tackle Football - Tiny Chiefs (7-8U)
  '7U': 'Tackle Football — Tiny Chiefs (7–8U)',
  '7-8U': 'Tackle Football — Tiny Chiefs (7–8U)',
  '8U': 'Tackle Football — Tiny Chiefs (7–8U)',
  'div_7u': 'Tackle Football — Tiny Chiefs (7–8U)',
  'div_8u': 'Tackle Football — Tiny Chiefs (7–8U)',
  'div_tiny_chiefs': 'Tackle Football — Tiny Chiefs (7–8U)',
  
  // Tackle Football - Mighty Chiefs (9U)
  '9U': 'Tackle Football — Mighty Chiefs (9U)',
  'div_9u': 'Tackle Football — Mighty Chiefs (9U)',
  'div_mighty_chiefs': 'Tackle Football — Mighty Chiefs (9U)',
  
  // Tackle Football - Junior Chiefs (10U)
  '10U': 'Tackle Football — Junior Chiefs (10U)',
  'div_10u': 'Tackle Football — Junior Chiefs (10U)',
  'div_junior_chiefs': 'Tackle Football — Junior Chiefs (10U)',
  
  // Tackle Football - Varsity Chiefs (11U)
  '11U': 'Tackle Football — Varsity Chiefs (11U)',
  'div_11u': 'Tackle Football — Varsity Chiefs (11U)',
  'div_varsity_chiefs': 'Tackle Football — Varsity Chiefs (11U)',
  
  // Tackle Football - Senior Chiefs (12U)
  '12U': 'Tackle Football — Senior Chiefs (12U)',
  'div_12u': 'Tackle Football — Senior Chiefs (12U)',
  'div_senior_chiefs': 'Tackle Football — Senior Chiefs (12U)',
  
  // Tackle Football - Elite Chiefs (13U)
  '13U': 'Tackle Football — Elite Chiefs (13U)',
  'div_13u': 'Tackle Football — Elite Chiefs (13U)',
  'div_elite_chiefs': 'Tackle Football — Elite Chiefs (13U)',
  
  // Cheer - The Lady Chiefs: Sparks (5-8)
  '5-8': 'Cheer — The Lady Chiefs: Cheer Sparks (5–8)',
  'div_cheer_sparks': 'Cheer — The Lady Chiefs: Cheer Sparks (5–8)',
  
  // Cheer - The Lady Chiefs: Stars (9-11)
  '9-11': 'Cheer — The Lady Chiefs: Cheer Stars (9–11)',
  'div_cheer_stars': 'Cheer — The Lady Chiefs: Cheer Stars (9–11)',
  
  // Cheer - The Lady Chiefs: Elite (12-14)
  '12-14': 'Cheer — The Lady Chiefs: Cheer Elite (12–14)',
  'div_cheer_elite': 'Cheer — The Lady Chiefs: Cheer Elite (12–14)'
};

// Get division display name
const getDivisionDisplay = (ageGroup) => {
  if (!ageGroup) return 'Not assigned';
  return DIVISION_DISPLAY[ageGroup] || ageGroup;
};

// Parse "YYYY-MM-DD" (or full-ISO with a 'T' suffix) as a LOCAL-time calendar
// date — never UTC — to avoid timezone-driven day shifting on display.
// `new Date("YYYY-MM-DD")` is parsed by the JS spec as UTC midnight, which
// renders as the previous calendar day in any timezone west of UTC.
const formatLocalDOB = (s) => {
  if (!s) return 'Not set';
  const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return 'Not set';
  return format(new Date(y, m - 1, d), 'MMM d, yyyy');
};

const FamilyPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [household, setHousehold] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Modal states
  const [showEditHousehold, setShowEditHousehold] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showEditPlayer, setShowEditPlayer] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Secondary parent states
  const [showInviteParent, setShowInviteParent] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Teacher management states
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [teacherForm, setTeacherForm] = useState({ name: '', email: '', school: '', subject: '' });
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [playerTeachers, setPlayerTeachers] = useState({});
  const [addingTeacherForPlayer, setAddingTeacherForPlayer] = useState(null);
  
  // School edit states
  const [showEditSchool, setShowEditSchool] = useState(false);
  const [editSchoolForm, setEditSchoolForm] = useState({ school: '', grade: '' });
  const [editingSchoolPlayer, setEditingSchoolPlayer] = useState(null);
  const [schoolLoading, setSchoolLoading] = useState(false);
  
  // Form states
  const [householdForm, setHouseholdForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    emergency_contact: ''
  });
  
  const [playerForm, setPlayerForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    grade: '',
    school: '',
    jersey_number: '',
    position: '',
    medical_notes: '',
    photo_url: ''
  });

  // SMS opt-in (Twilio MVP)
  const [smsPrefs, setSmsPrefs] = useState({ sms_enabled: false, consent_status: 'unset', phone_e164: null, sms_service_enabled: false });
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsError, setSmsError] = useState('');

  const [registrations, setRegistrations] = useState({});

  // Coaching Staff (parent view) — fetched from existing /api/teams/{id}/detail
  // when the parent opens a player detail panel and the player has a team_id.
  // Phase Role Access permits parent reads scoped to their child's team.
  const [coachingStaff, setCoachingStaff] = useState({ coaches: [], team_moms_detail: [] });
  const [coachingStaffLoading, setCoachingStaffLoading] = useState(false);

  useEffect(() => {
    fetchFamilyData();
    fetchSmsPrefs();
  }, []);

  const fetchSmsPrefs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/sms-preferences`, { withCredentials: true });
      setSmsPrefs(res.data || {});
    } catch (e) {
      // Non-fatal — UI just shows opt-in as off.
    }
  };

  const handleSmsToggle = async (next) => {
    setSmsError('');
    setSmsSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/notifications/sms-preferences`,
        { sms_enabled: next },
        { withCredentials: true }
      );
      setSmsPrefs(prev => ({ ...prev, ...res.data }));
    } catch (e) {
      setSmsError(e?.response?.data?.detail || 'Could not update SMS preference.');
    } finally {
      setSmsSaving(false);
    }
  };

  // Fetch coaching staff whenever a player with a team is selected.
  useEffect(() => {
    let cancelled = false;
    if (!selectedPlayer?.team_id) {
      setCoachingStaff({ coaches: [], team_moms_detail: [] });
      return;
    }
    setCoachingStaffLoading(true);
    axios
      .get(`${API_URL}/api/teams/${selectedPlayer.team_id}/detail`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setCoachingStaff({
          coaches: res.data?.coaches || [],
          team_moms_detail: res.data?.team_moms_detail || [],
        });
      })
      .catch(() => {
        if (!cancelled) setCoachingStaff({ coaches: [], team_moms_detail: [] });
      })
      .finally(() => {
        if (!cancelled) setCoachingStaffLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPlayer?.team_id]);

  const fetchFamilyData = async () => {
    try {
      setError(null);
      const [householdRes, playersRes, regRes] = await Promise.all([
        axios.get(`${API_URL}/api/household`, { withCredentials: true }),
        axios.get(`${API_URL}/api/players`, { withCredentials: true }),
        axios.get(`${API_URL}/api/household/registrations`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setHousehold(householdRes.data);
      setPlayers(playersRes.data || []);
      
      // Map registrations by player_id for quick lookup.
      // Feb 2026 P0 sync fix: on player_id collision, an approved
      // registration MUST win over an incomplete/in-progress duplicate so
      // the parent Family page reflects the paid-and-approved state even
      // if the API returns the incomplete row last.
      const regMap = {};
      const _rank = (s) => {
        if (s === 'approved') return 4;
        if (s === 'submitted' || s === 'pending_review') return 3;
        if (s === 'in_progress') return 2;
        if (s === 'incomplete' || s === 'not_started') return 1;
        return 0;
      };
      (regRes.data || []).forEach(reg => {
        const existing = regMap[reg.player_id];
        if (!existing || _rank(reg.status) >= _rank(existing.status)) {
          regMap[reg.player_id] = reg;
        }
      });
      setRegistrations(regMap);
      
      // Initialize household form
      const h = householdRes.data;
      // Pre-fill First / Last from the authoritative display name. Source
      // priority mirrors the household-card render: primary_contact_name → user.name.
      const fullName = ((h?.is_primary
        ? h?.primary_contact_name
        : h?.secondary_contact_name) || h?.primary_contact_name || user?.name || '').trim();
      const parts = fullName.trim().split(' ');
      const first = parts.shift() || '';
      const last = parts.join(' ');
      setHouseholdForm({
        first_name: first,
        last_name: last,
        phone: h?.phone || '',
        street: h?.address?.street || '',
        city: h?.address?.city || '',
        state: h?.address?.state || '',
        zip: h?.address?.zip || '',
        emergency_contact: h?.emergency_contact || ''
      });
    } catch (err) {
      console.error('Failed to fetch family data:', err);
      setError('Unable to load family information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHousehold = async () => {
    const first = (householdForm.first_name || '').trim();
    const last = (householdForm.last_name || '').trim();
    if (!first || !last) {
      alert('First name and last name are both required');
      return;
    }
    const fullName = `${first} ${last}`.trim();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/household`, {
        name: fullName,
        phone: householdForm.phone,
        address: {
          street: householdForm.street,
          city: householdForm.city,
          state: householdForm.state,
          zip: householdForm.zip
        },
        emergency_contact: householdForm.emergency_contact
      }, { withCredentials: true });
      
      setSuccessMessage('Household information updated successfully!');
      setShowEditHousehold(false);
      // Refresh both the family/household data AND the auth context so the
      // navbar, sidebar, and dropdown reflect the new name immediately.
      await Promise.all([fetchFamilyData(), checkAuth()]);
    } catch (err) {
      console.error('Failed to save household:', err);
      alert(err.response?.data?.detail || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPlayer = async () => {
    if (!playerForm.first_name || !playerForm.last_name) {
      alert('Please enter the player\'s first and last name');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/players`, {
        ...playerForm,
        household_id: household?.household_id
      }, { withCredentials: true });
      
      setSuccessMessage(`${playerForm.first_name} has been added to your family!`);
      setShowAddPlayer(false);
      setPlayerForm({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        grade: '',
        school: '',
        jersey_number: '',
        position: '',
        medical_notes: '',
        photo_url: ''
      });
      fetchFamilyData();
    } catch (err) {
      console.error('Failed to add player:', err);
      alert(err.response?.data?.detail || 'Failed to add player. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPlayer = async () => {
    if (!playerForm.first_name || !playerForm.last_name || !editingPlayerId) {
      alert('Please enter first and last name');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/players/${editingPlayerId}`,
        playerForm,
        { withCredentials: true }
      );
      setShowEditPlayer(false);
      setEditingPlayerId(null);
      setPlayerForm({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        grade: '',
        school: '',
        jersey_number: '',
        position: '',
        medical_notes: '',
        photo_url: ''
      });
      setSuccessMessage('Player updated successfully!');
      await fetchFamilyData();
    } catch (err) {
      console.error('Failed to update player:', err);
      alert(err.response?.data?.detail || 'Failed to update player. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openPlayerDetail = (player) => {
    setSelectedPlayer(player);
    // Automatically fetch teachers when opening player detail
    fetchPlayerTeachers(player.player_id);
  };

  const handleDeletePlayer = async () => {
    if (!playerToDelete) return;
    
    // Verify the confirmation text matches
    const expectedText = `DELETE ${playerToDelete.first_name}`.toUpperCase();
    if (deleteConfirmText.toUpperCase() !== expectedText) {
      alert(`Please type "DELETE ${playerToDelete.first_name}" to confirm.`);
      return;
    }
    
    setSaving(true);
    try {
      await axios.delete(
        `${API_URL}/api/players/${playerToDelete.player_id}`,
        { withCredentials: true }
      );
      setShowDeleteConfirm(false);
      setPlayerToDelete(null);
      setDeleteConfirmText('');
      setSelectedPlayer(null);
      setSuccessMessage(`${playerToDelete.first_name} has been removed from your household.`);
      await fetchFamilyData();
    } catch (err) {
      console.error('Failed to delete player:', err);
      alert(err.response?.data?.detail || 'Failed to remove player. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (player) => {
    setPlayerToDelete(player);
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };

  // Secondary Parent Functions
  const handleInviteParent = async () => {
    if (!inviteForm.email) {
      alert('Please enter an email address');
      return;
    }
    
    setInviteLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/household/invite-parent`, {
        email: inviteForm.email,
        name: inviteForm.name
      }, { withCredentials: true });
      
      setSuccessMessage(res.data.message);
      setShowInviteParent(false);
      setInviteForm({ email: '', name: '' });
      fetchFamilyData();
    } catch (err) {
      console.error('Failed to invite parent:', err);
      alert(err.response?.data?.detail || 'Failed to send invitation. Please try again.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveSecondaryParent = async () => {
    if (!confirm('Are you sure you want to remove the secondary parent from this household?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/household/secondary-parent`, { withCredentials: true });
      setSuccessMessage('Secondary parent removed from household');
      fetchFamilyData();
    } catch (err) {
      console.error('Failed to remove secondary parent:', err);
      alert(err.response?.data?.detail || 'Failed to remove secondary parent.');
    }
  };

  const handleCancelInvite = async () => {
    try {
      await axios.delete(`${API_URL}/api/household/cancel-invite`, { withCredentials: true });
      setSuccessMessage('Invitation cancelled');
      fetchFamilyData();
    } catch (err) {
      console.error('Failed to cancel invite:', err);
      alert(err.response?.data?.detail || 'Failed to cancel invitation.');
    }
  };

  // Teacher Management Functions
  const fetchPlayerTeachers = async (playerId) => {
    try {
      const res = await axios.get(`${API_URL}/api/players/${playerId}/teachers`, { withCredentials: true });
      setPlayerTeachers(prev => ({
        ...prev,
        [playerId]: res.data.teachers || []
      }));
    } catch (err) {
      console.error('Failed to fetch teachers:', err);
    }
  };

  const handleOpenAddTeacher = (player) => {
    setAddingTeacherForPlayer(player);
    setTeacherForm({ name: '', email: '', school: '', subject: '' });
    setShowAddTeacher(true);
    // Fetch existing teachers if not already loaded
    if (!playerTeachers[player.player_id]) {
      fetchPlayerTeachers(player.player_id);
    }
  };

  const handleAddTeacher = async () => {
    if (!teacherForm.name || !teacherForm.email) {
      alert("Teacher's name and email are required");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(teacherForm.email)) {
      alert('Please enter a valid email address');
      return;
    }

    setTeacherLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/players/${addingTeacherForPlayer.player_id}/teachers`,
        teacherForm,
        { withCredentials: true }
      );
      
      setSuccessMessage(`${teacherForm.name} has been added as a teacher for ${addingTeacherForPlayer.first_name}!`);
      setShowAddTeacher(false);
      setTeacherForm({ name: '', email: '', school: '', subject: '' });
      
      // Refresh teachers list
      await fetchPlayerTeachers(addingTeacherForPlayer.player_id);
      setAddingTeacherForPlayer(null);
    } catch (err) {
      console.error('Failed to add teacher:', err);
      alert(err.response?.data?.detail || 'Failed to add teacher. Please try again.');
    } finally {
      setTeacherLoading(false);
    }
  };

  const handleRemoveTeacher = async (playerId, teacherId, teacherName) => {
    if (!confirm(`Are you sure you want to remove ${teacherName} from this player's teachers?`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_URL}/api/players/${playerId}/teachers/${teacherId}`,
        { withCredentials: true }
      );
      setSuccessMessage(`${teacherName} has been removed.`);
      await fetchPlayerTeachers(playerId);
    } catch (err) {
      console.error('Failed to remove teacher:', err);
      alert(err.response?.data?.detail || 'Failed to remove teacher.');
    }
  };

  // School Edit Functions
  const handleOpenEditSchool = (player) => {
    setEditingSchoolPlayer(player);
    setEditSchoolForm({
      school: player.school || '',
      grade: player.grade || ''
    });
    setShowEditSchool(true);
  };

  const handleSaveSchool = async () => {
    if (!editingSchoolPlayer) return;
    
    setSchoolLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/players/${editingSchoolPlayer.player_id}/school`,
        editSchoolForm,
        { withCredentials: true }
      );
      
      setSuccessMessage(`School information updated for ${editingSchoolPlayer.first_name}!`);
      setShowEditSchool(false);
      setEditingSchoolPlayer(null);
      
      // Refresh player data
      fetchFamilyData();
      
      // Update selected player if it's the same one
      if (selectedPlayer && selectedPlayer.player_id === editingSchoolPlayer.player_id) {
        setSelectedPlayer(prev => ({
          ...prev,
          school: editSchoolForm.school,
          grade: editSchoolForm.grade
        }));
      }
    } catch (err) {
      console.error('Failed to update school:', err);
      alert(err.response?.data?.detail || 'Failed to update school information.');
    } finally {
      setSchoolLoading(false);
    }
  };

  const getEligibilityColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'eligible':
      case 'cleared':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending_docs':
      case 'pending_verification':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending_payment':
      case 'not_eligible':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'waitlisted':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getEligibilityIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'eligible':
      case 'cleared':
        return CheckCircle;
      case 'pending_docs':
      case 'pending_verification':
      case 'pending_payment':
        return Clock;
      case 'not_eligible':
        return AlertCircle;
      default:
        return FileText;
    }
  };

  const formatEligibilityLabel = (status) => {
    const labels = {
      'eligible': 'Cleared to Play',
      'cleared': 'Cleared to Play',
      'pending_docs': 'Documents Needed',
      'pending_payment': 'Payment Required',
      'pending_verification': 'Verification Pending',
      'waitlisted': 'On Waitlist',
      'not_eligible': 'Not Eligible',
      'registration_incomplete': 'Registration Incomplete'
    };
    return labels[status?.toLowerCase()] || status || 'Unknown';
  };

  // Calculate family stats
  const totalChildren = players.length;
  const clearedChildren = players.filter(p => 
    p.eligibility_status === 'eligible' || p.eligibility_status === 'cleared'
  ).length;
  const pendingActions = players.filter(p => 
    ['pending_docs', 'pending_payment', 'pending_verification', 'registration_incomplete'].includes(p.eligibility_status)
  ).length;

  // Edit Household Modal - rendered inline to avoid focus loss
  const renderEditHouseholdModal = () => {
    if (!showEditHousehold) return null;
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-navy">Edit Household Information</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowEditHousehold(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-4 space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Your Name</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">First Name *</label>
                  <Input
                    value={householdForm.first_name}
                    onChange={(e) => setHouseholdForm(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="First name"
                    data-testid="household-first-name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Last Name *</label>
                  <Input
                    value={householdForm.last_name}
                    onChange={(e) => setHouseholdForm(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Last name"
                    data-testid="household-last-name"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <Input
                value={householdForm.phone}
                onChange={(e) => setHouseholdForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(203) 555-0123"
                data-testid="household-phone"
              />
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Address</p>
              <div className="space-y-3">
                <Input
                  value={householdForm.street}
                  onChange={(e) => setHouseholdForm(prev => ({ ...prev, street: e.target.value }))}
                  placeholder="Street Address"
                  data-testid="household-street"
                />
                <div className="grid grid-cols-6 gap-2">
                  <Input
                    className="col-span-3"
                    value={householdForm.city}
                    onChange={(e) => setHouseholdForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="City"
                  />
                  <Input
                    className="col-span-1"
                    value={householdForm.state}
                    onChange={(e) => setHouseholdForm(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="State"
                    maxLength={2}
                  />
                  <Input
                    className="col-span-2"
                    value={householdForm.zip}
                    onChange={(e) => setHouseholdForm(prev => ({ ...prev, zip: e.target.value }))}
                    placeholder="ZIP"
                    maxLength={5}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
              <Input
                value={householdForm.emergency_contact}
                onChange={(e) => setHouseholdForm(prev => ({ ...prev, emergency_contact: e.target.value }))}
                placeholder="Name and phone number"
                data-testid="household-emergency"
              />
            </div>
            
            <div className="flex space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowEditHousehold(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveHousehold}
                disabled={saving || !householdForm.first_name?.trim() || !householdForm.last_name?.trim()}
                className="flex-1 bg-navy hover:bg-navy/90"
                data-testid="save-household-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add Player Modal - rendered inline to avoid focus loss
  const renderAddPlayerModal = () => {
    if (!showAddPlayer) return null;
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-navy">Add New Player</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowAddPlayer(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Player Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Player Photo (Optional)</label>
              <PhotoUpload
                currentPhotoUrl={playerForm.photo_url}
                firstName={playerForm.first_name}
                lastName={playerForm.last_name}
                size="lg"
                onPhotoChange={(photoUrl) => setPlayerForm(prev => ({ ...prev, photo_url: photoUrl }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <Input
                  value={playerForm.first_name}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="First name"
                  data-testid="player-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <Input
                  value={playerForm.last_name}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Last name"
                  data-testid="player-last-name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <Input
                type="date"
                value={playerForm.date_of_birth}
                onChange={(e) => setPlayerForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                data-testid="player-dob"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                <select
                  value={playerForm.grade}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">Select grade...</option>
                  <option value="Pre-K">Pre-K</option>
                  <option value="K">Kindergarten</option>
                  <option value="1st">1st Grade</option>
                  <option value="2nd">2nd Grade</option>
                  <option value="3rd">3rd Grade</option>
                  <option value="4th">4th Grade</option>
                  <option value="5th">5th Grade</option>
                  <option value="6th">6th Grade</option>
                  <option value="7th">7th Grade</option>
                  <option value="8th">8th Grade</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                <Input
                  value={playerForm.school}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, school: e.target.value }))}
                  placeholder="School name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jersey Number</label>
                <Input
                  value={playerForm.jersey_number}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, jersey_number: e.target.value }))}
                  placeholder="e.g., 12"
                  maxLength={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <select
                  value={playerForm.position}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, position: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">Select position...</option>
                  <option value="Quarterback">Quarterback</option>
                  <option value="Running Back">Running Back</option>
                  <option value="Wide Receiver">Wide Receiver</option>
                  <option value="Tight End">Tight End</option>
                  <option value="Offensive Line">Offensive Line</option>
                  <option value="Defensive Line">Defensive Line</option>
                  <option value="Linebacker">Linebacker</option>
                  <option value="Defensive Back">Defensive Back</option>
                  <option value="Kicker">Kicker</option>
                  <option value="All Positions">All Positions</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Medical Notes / Allergies</label>
              <Textarea
                value={playerForm.medical_notes}
                onChange={(e) => setPlayerForm(prev => ({ ...prev, medical_notes: e.target.value }))}
                placeholder="Any medical conditions, allergies, or notes coaches should know..."
                rows={3}
              />
            </div>
            
            <div className="flex space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowAddPlayer(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddPlayer}
                disabled={saving || !playerForm.first_name || !playerForm.last_name}
                className="flex-1 bg-navy hover:bg-navy/90"
                data-testid="add-player-btn"
              >
                {saving ? 'Adding...' : 'Add Player'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Edit Player Modal
  const renderEditPlayerModal = () => {
    if (!showEditPlayer) return null;
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-navy">Edit Player</h2>
            <Button variant="ghost" size="sm" onClick={() => { setShowEditPlayer(false); setEditingPlayerId(null); }}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Player Photo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Player Photo</label>
              <PhotoUpload
                currentPhotoUrl={playerForm.photo_url}
                firstName={playerForm.first_name}
                lastName={playerForm.last_name}
                size="lg"
                onPhotoChange={(photoUrl) => setPlayerForm(prev => ({ ...prev, photo_url: photoUrl }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <Input
                  value={playerForm.first_name}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <Input
                  value={playerForm.last_name}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Last name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <Input
                type="date"
                value={playerForm.date_of_birth}
                onChange={(e) => setPlayerForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                <Input
                  value={playerForm.grade}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, grade: e.target.value }))}
                  placeholder="e.g., 5th"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                <Input
                  value={playerForm.school}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, school: e.target.value }))}
                  placeholder="School name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jersey Number</label>
                <Input
                  value={playerForm.jersey_number}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, jersey_number: e.target.value }))}
                  placeholder="e.g., 12"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <Input
                  value={playerForm.position}
                  onChange={(e) => setPlayerForm(prev => ({ ...prev, position: e.target.value }))}
                  placeholder="e.g., Running Back"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Medical Notes</label>
              <Textarea
                value={playerForm.medical_notes}
                onChange={(e) => setPlayerForm(prev => ({ ...prev, medical_notes: e.target.value }))}
                placeholder="Any allergies, conditions, or medical information..."
                rows={3}
              />
            </div>
            
            <div className="flex space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => { setShowEditPlayer(false); setEditingPlayerId(null); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditPlayer}
                disabled={saving || !playerForm.first_name || !playerForm.last_name}
                className="flex-1 bg-navy hover:bg-navy/90"
                data-testid="save-player-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Delete Confirmation Modal
  const renderDeleteConfirmModal = () => {
    if (!showDeleteConfirm || !playerToDelete) return null;
    
    const expectedText = `DELETE ${playerToDelete.first_name}`.toUpperCase();
    const isConfirmEnabled = deleteConfirmText.toUpperCase() === expectedText;
    
    return (
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => {
        setShowDeleteConfirm(false);
        setPlayerToDelete(null);
        setDeleteConfirmText('');
      }}>
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="bg-red-600 text-white p-4 rounded-t-xl">
            <h2 className="text-lg font-bold flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Remove Child from Household
            </h2>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-semibold mb-2">This action cannot be undone!</p>
              <p className="text-red-700 text-sm">
                You are about to permanently remove <strong>{playerToDelete.first_name} {playerToDelete.last_name}</strong> from your household. 
                This will also delete all associated registration data, documents, and waivers.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <strong className="text-red-600">DELETE {playerToDelete.first_name.toUpperCase()}</strong> to confirm:
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={`DELETE ${playerToDelete.first_name.toUpperCase()}`}
                className="border-red-300 focus:border-red-500 focus:ring-red-500"
                data-testid="delete-confirm-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPlayerToDelete(null);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeletePlayer}
                disabled={saving || !isConfirmEnabled}
                variant={isConfirmEnabled ? "destructive" : "secondary"}
                data-testid="confirm-delete-btn"
              >
                {saving ? 'Removing...' : 'Remove Permanently'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Player Detail Modal - rendered inline to avoid focus loss
  const renderPlayerDetailModal = () => {
    if (!selectedPlayer) return null;
    const EligibilityIcon = getEligibilityIcon(selectedPlayer.eligibility_status);
    const playerReg = registrations[selectedPlayer.player_id];
    const isEditable = !playerReg || ['not_started', 'in_progress', 'submitted', 'pending_review', 'incomplete', 'denied'].includes(playerReg?.status);
    
    const getRegStatusBadge = (status) => {
      const map = {
        'not_started': { label: 'Not Started', className: 'bg-gray-100 text-gray-800' },
        'in_progress': { label: 'In Progress', className: 'bg-blue-100 text-blue-800' },
        'submitted': { label: 'Submitted', className: 'bg-purple-100 text-purple-800' },
        'pending_review': { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800' },
        'incomplete': { label: 'Incomplete', className: 'bg-orange-100 text-orange-800' },
        'approved': { label: 'Approved', className: 'bg-green-100 text-green-800' },
        'denied': { label: 'Denied', className: 'bg-red-100 text-red-800' },
        'waitlisted': { label: 'Waitlisted', className: 'bg-indigo-100 text-indigo-800' },
        'archived': { label: 'Archived', className: 'bg-gray-200 text-gray-600' }
      };
      return map[status] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-800' };
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-navy">Player Details</h2>
            <Button variant="ghost" size="sm" onClick={() => setSelectedPlayer(null)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-4">
            {/* Player Header with Photo */}
            <div className="flex items-center space-x-4 mb-4">
              <PlayerAvatar
                photoUrl={selectedPlayer.photo_url}
                firstName={selectedPlayer.first_name}
                lastName={selectedPlayer.last_name}
                size="xl"
              />
              <div>
                <h3 className="text-xl font-bold text-navy">
                  {selectedPlayer.first_name} {selectedPlayer.last_name}
                </h3>
                {selectedPlayer.jersey_number && (
                  <Badge className="bg-gold/20 text-navy font-bold">
                    #{selectedPlayer.jersey_number}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Photo Upload - only if editable */}
            {isEditable && (
              <div className="mb-4 p-3 rounded-lg border bg-gray-50">
                <p className="text-sm font-medium text-gray-700 mb-2">Player Photo</p>
                <PhotoUpload
                  currentPhotoUrl={selectedPlayer.photo_url}
                  firstName={selectedPlayer.first_name}
                  lastName={selectedPlayer.last_name}
                  size="md"
                  disabled={!isEditable}
                  onPhotoChange={async (photoUrl) => {
                    try {
                      if (photoUrl) {
                        await axios.put(
                          `${API_URL}/api/players/${selectedPlayer.player_id}/photo`,
                          { photo_url: photoUrl },
                          { withCredentials: true }
                        );
                      } else {
                        await axios.delete(
                          `${API_URL}/api/players/${selectedPlayer.player_id}/photo`,
                          { withCredentials: true }
                        );
                      }
                      // Update local state
                      setSelectedPlayer(prev => ({ ...prev, photo_url: photoUrl }));
                      setPlayers(prev => prev.map(p => 
                        p.player_id === selectedPlayer.player_id 
                          ? { ...p, photo_url: photoUrl }
                          : p
                      ));
                    } catch (err) {
                      console.error('Failed to update photo:', err);
                      alert(err.response?.data?.detail || 'Failed to update photo');
                    }
                  }}
                />
              </div>
            )}
            
            {/* Registration Status with Editable Messaging */}
            {playerReg && (
              <div className="mb-4 p-3 rounded-lg border bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Registration Status</span>
                  <Badge className={getRegStatusBadge(playerReg.status).className}>
                    {getRegStatusBadge(playerReg.status).label}
                  </Badge>
                </div>
                {isEditable ? (
                  <p className="text-sm text-blue-600 flex items-center">
                    <Edit className="w-4 h-4 mr-1" />
                    You can still update this player's details until league verification is complete.
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 flex items-center">
                    <Shield className="w-4 h-4 mr-1" />
                    This registration is now locked because it has been verified.
                  </p>
                )}
              </div>
            )}
            
            {/* Parent Note / Missing Items Alert */}
            {playerReg?.parent_note && (
              <div className="mb-4 p-3 rounded-lg border border-orange-200 bg-orange-50">
                <p className="text-sm font-medium text-orange-800 flex items-center mb-1">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Action Required
                </p>
                <p className="text-sm text-orange-700">{playerReg.parent_note}</p>
              </div>
            )}
            
            {/* Eligibility Status */}
            <div className={`flex items-center space-x-2 p-3 rounded-lg border mb-4 ${getEligibilityColor(selectedPlayer.eligibility_status)}`}>
              <EligibilityIcon className="w-5 h-5" />
              <span className="font-medium">
                {formatEligibilityLabel(selectedPlayer.eligibility_status)}
              </span>
            </div>
            
            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Date of Birth</p>
                <p className="font-medium text-navy">
                  {formatLocalDOB(selectedPlayer.date_of_birth)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Division</p>
                <div className="flex items-center gap-2">
                  {getDivisionLogo(selectedPlayer.age_group) && (
                    <img 
                      src={getDivisionLogo(selectedPlayer.age_group)} 
                      alt="" 
                      className="w-10 h-10 object-contain"
                    />
                  )}
                  <p className="font-medium text-navy">{getDivisionDisplay(selectedPlayer.age_group)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Grade</p>
                <p className="font-medium text-navy">{selectedPlayer.grade || 'Not set'}</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500 uppercase">School</p>
                  <button
                    onClick={() => handleOpenEditSchool(selectedPlayer)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                    data-testid="edit-school-btn"
                  >
                    Edit
                  </button>
                </div>
                <p className="font-medium text-navy">{selectedPlayer.school || 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Position</p>
                <p className="font-medium text-navy">{selectedPlayer.position || 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Team</p>
                <p className="font-medium text-navy">{selectedPlayer.team_name || 'Not assigned'}</p>
              </div>
            </div>

            {/* Coaching Staff (parent view) — shown only when a team is assigned. */}
            {selectedPlayer.team_id && (
              <div className="mb-4 p-3 rounded-lg border border-gray-200 bg-gray-50" data-testid="coaching-staff-section">
                <p className="text-xs text-gray-500 uppercase mb-2 font-semibold tracking-wider">Coaching Staff</p>
                {coachingStaffLoading ? (
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2" />
                ) : (coachingStaff.coaches.length === 0 && coachingStaff.team_moms_detail.length === 0) ? (
                  <p className="text-sm text-gray-500 italic" data-testid="coaching-staff-empty">
                    Coaching staff has not been assigned yet.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {coachingStaff.coaches.map((c) => {
                      const isHead = c.coach_role === 'Head Coach';
                      return (
                        <div
                          key={`p-staff-coach-${c.user_id}`}
                          className="flex items-center gap-2 bg-white px-2.5 py-1.5 rounded-md border border-gray-200"
                          data-testid={isHead ? `staff-head-coach-${c.user_id}` : `staff-assistant-${c.user_id}`}
                        >
                          <div className="w-7 h-7 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">
                              {c.name?.split(' ').map((n) => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-navy leading-tight">{c.name}</p>
                            <p className="text-xs text-gray-500 leading-tight">{c.coach_role}</p>
                          </div>
                        </div>
                      );
                    })}
                    {coachingStaff.team_moms_detail.map((m) => (
                      <div
                        key={`p-staff-mom-${m.user_id}`}
                        className="flex items-center gap-2 bg-white px-2.5 py-1.5 rounded-md border border-pink-200"
                        data-testid={`staff-team-mom-${m.user_id}`}
                      >
                        <div className="w-7 h-7 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">
                            {m.name?.split(' ').map((n) => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy leading-tight">{m.name}</p>
                          <p className="text-xs text-pink-600 leading-tight">Team Mom</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Payment Status */}
            {playerReg && (
              <div className="mb-4 p-3 rounded-lg border bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Payment</span>
                  <Badge className={
                    ['confirmed', 'paid', 'waived'].includes(playerReg.payment_status) 
                      ? 'bg-green-100 text-green-800' 
                      : playerReg.payment_status === 'submitted'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }>
                    {playerReg.payment_status === 'confirmed' || playerReg.payment_status === 'paid' ? 'Confirmed' 
                      : playerReg.payment_status === 'waived' ? 'Waived'
                      : playerReg.payment_status === 'submitted' ? 'Submitted - Awaiting Confirmation'
                      : 'Pending'}
                  </Badge>
                </div>
                
                {/* Program Type */}
                {playerReg.registration_data?.program_type?.program_type && (
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Program:</span>
                    <span className="font-medium text-navy capitalize">
                      {playerReg.registration_data.program_type.program_type.replace('_', ' ')}
                    </span>
                  </div>
                )}
                
                {/* Amount */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-medium">
                    ${playerReg.amount_paid?.toFixed(2) || '0.00'} / ${playerReg.total_amount?.toFixed(2) || '0.00'}
                  </span>
                </div>
                
                {/* Payment Messages */}
                {['confirmed', 'paid', 'waived'].includes(playerReg.payment_status) ? (
                  <p className="text-xs text-green-600 mt-2 flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Payment complete. Registration can proceed.
                  </p>
                ) : playerReg.payment_status === 'submitted' ? (
                  <p className="text-xs text-blue-600 mt-2 flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    Payment submitted. Manual payments may take 1-2 business days to confirm.
                  </p>
                ) : (
                  <p className="text-xs text-yellow-600 mt-2 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Registration is not complete until payment is confirmed.
                  </p>
                )}
              </div>
            )}
            
            {/* Medical Notes */}
            {selectedPlayer.medical_notes && (
              <div className="bg-red/5 border border-red/20 rounded-lg p-3 mb-4">
                <p className="text-xs text-red uppercase font-medium mb-1">Medical Notes</p>
                <p className="text-sm text-gray-700">{selectedPlayer.medical_notes}</p>
              </div>
            )}
            
            {/* Teacher Management Section */}
            <div className="mb-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <GraduationCap className="w-5 h-5 text-navy mr-2" />
                  <span className="font-semibold text-navy">Teachers</span>
                  <Badge className="ml-2 bg-navy/10 text-navy text-xs">
                    {playerTeachers[selectedPlayer.player_id]?.length || 0}/3
                  </Badge>
                </div>
                {(playerTeachers[selectedPlayer.player_id]?.length || 0) < 3 && (
                  <Button
                    size="sm"
                    onClick={() => handleOpenAddTeacher(selectedPlayer)}
                    className="bg-gold text-navy hover:bg-gold/90 text-xs"
                    data-testid="add-teacher-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Teacher
                  </Button>
                )}
              </div>
              
              <p className="text-xs text-gray-500 mb-3">
                Add your child's teachers (up to 3) for weekly academic/behavior check-ins.
              </p>
              
              {!playerTeachers[selectedPlayer.player_id] ? (
                <button 
                  onClick={() => fetchPlayerTeachers(selectedPlayer.player_id)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Load teachers...
                </button>
              ) : playerTeachers[selectedPlayer.player_id].length === 0 ? (
                <p className="text-sm text-gray-500 italic">No teachers added yet.</p>
              ) : (
                <div className="space-y-2">
                  {playerTeachers[selectedPlayer.player_id].map((teacher) => (
                    <div 
                      key={teacher.teacher_id} 
                      className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm text-gray-900">{teacher.name}</p>
                        <p className="text-xs text-gray-500">{teacher.email}</p>
                        {(teacher.school || teacher.subject) && (
                          <p className="text-xs text-gray-400">
                            {teacher.school}{teacher.school && teacher.subject ? ' • ' : ''}{teacher.subject}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveTeacher(selectedPlayer.player_id, teacher.teacher_id, teacher.name)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove teacher"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="space-y-3 pt-4 border-t">
              {/* Edit Player Button - only if editable */}
              {isEditable && (
                <Button
                  onClick={() => {
                    setSelectedPlayer(null);
                    // Open edit mode with this player's data
                    setPlayerForm({
                      first_name: selectedPlayer.first_name || '',
                      last_name: selectedPlayer.last_name || '',
                      date_of_birth: selectedPlayer.date_of_birth?.split('T')[0] || '',
                      grade: selectedPlayer.grade || '',
                      school: selectedPlayer.school || '',
                      jersey_number: selectedPlayer.jersey_number || '',
                      position: selectedPlayer.position || '',
                      medical_notes: selectedPlayer.medical_notes || '',
                      photo_url: selectedPlayer.photo_url || ''
                    });
                    setEditingPlayerId(selectedPlayer.player_id);
                    setShowEditPlayer(true);
                  }}
                  className="w-full bg-navy hover:bg-navy/90"
                  data-testid="edit-player-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Player Details
                </Button>
              )}
              
              {/* Continue Registration Button */}
              {playerReg && !['approved', 'archived'].includes(playerReg.status) && (
                <Button
                  onClick={() => navigate(`/registration/${playerReg.registration_id}`)}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="continue-registration-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {playerReg.status === 'not_started' ? 'Start Registration' : 'Continue Registration'}
                </Button>
              )}
              
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setSelectedPlayer(null)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={() => navigate('/schedule')}
                  variant="outline"
                  className="flex-1"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule
                </Button>
              </div>
              
              {/* Remove Child Button */}
              {isEditable && (
                <Button
                  variant="outline"
                  onClick={() => {
                    openDeleteConfirm(selectedPlayer);
                  }}
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 mt-3"
                  data-testid="remove-child-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Remove This Child
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <LoadingSkeleton rows={4} />
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="family-page">
        {/* Athletic Header */}
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Your Roster</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter mb-1">My Family</h1>
          <p className="text-gray-600 font-body">Manage your household information and player profiles</p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6">
            <SuccessMessage 
              title={successMessage} 
              onDismiss={() => setSuccessMessage(null)} 
            />
          </div>
        )}

        {error && (
          <div className="mb-6">
            <WarningBanner 
              title="Unable to load data"
              description={error}
              action={fetchFamilyData}
              actionLabel="Retry"
            />
          </div>
        )}

        {/* Family Stats - Athletic Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
            <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center mb-3">
              <Users className="w-5 h-5 text-navy" />
            </div>
            <p className="font-display text-3xl font-black text-navy tracking-tight">{totalChildren}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Children</p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <p className="font-display text-3xl font-black text-green-600 tracking-tight">{clearedChildren}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Cleared</p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${pendingActions > 0 ? 'bg-gold/20' : 'bg-gray-100'}`}>
              <Clock className={`w-5 h-5 ${pendingActions > 0 ? 'text-gold' : 'text-gray-500'}`} />
            </div>
            <p className={`font-display text-3xl font-black tracking-tight ${pendingActions > 0 ? 'text-gold' : 'text-gray-500'}`}>{pendingActions}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Action Needed</p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${household?.balance > 0 ? 'bg-red/10' : 'bg-green-100'}`}>
              <CreditCard className={`w-5 h-5 ${household?.balance > 0 ? 'text-red' : 'text-green-600'}`} />
            </div>
            <p className={`font-display text-3xl font-black tracking-tight ${household?.balance > 0 ? 'text-red' : 'text-green-600'}`}>${household?.balance?.toFixed(0) || '0'}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Balance</p>
          </div>
        </div>

        {/* Pending Actions Alert */}
        {pendingActions > 0 && (
          <div className="mb-6">
            <WarningBanner
              title={`${pendingActions} player${pendingActions > 1 ? 's' : ''} need attention`}
              description="Complete the required steps to get your players cleared for the season."
            />
          </div>
        )}

        {/* Household Information */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6" data-testid="household-card">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight flex items-center">
                <Home className="w-5 h-5 mr-2" />
                Household Information
              </h2>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-navy border-2 border-gray-200 hover:border-navy font-bold uppercase tracking-wider text-xs"
                onClick={() => setShowEditHousehold(true)}
                data-testid="edit-household-btn"
              >
                <Edit className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            </div>
          </div>
          <div className="p-5">
            {household ? (
              <div className="space-y-6">
                {/* Parents/Guardians Section */}
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    Parents / Guardians
                    <Badge className="ml-2 bg-navy/10 text-navy text-[10px]">
                      {household.secondary_contact_id ? '2/2' : '1/2'}
                    </Badge>
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Primary Parent */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <Badge className="bg-navy text-white text-[10px]">Primary</Badge>
                        {household.is_primary && (
                          <Badge className="bg-gold/20 text-navy text-[10px]">You</Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center text-white font-bold">
                          {(household.primary_contact_name || user?.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-navy">{household.primary_contact_name || user?.name}</p>
                          <p className="text-xs text-gray-500">{household.email || user?.email}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Secondary Parent or Invite */}
                    {household.secondary_contact_id ? (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <Badge className="bg-light-blue text-white text-[10px]">Secondary</Badge>
                          {!household.is_primary && (
                            <Badge className="bg-gold/20 text-navy text-[10px]">You</Badge>
                          )}
                          {household.is_primary && (
                            <button 
                              onClick={handleRemoveSecondaryParent}
                              className="text-red hover:text-red/80 text-xs flex items-center"
                              data-testid="remove-secondary-btn"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-light-blue flex items-center justify-center text-white font-bold">
                            {(household.secondary_contact_name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-navy">{household.secondary_contact_name}</p>
                            <p className="text-xs text-gray-500">{household.secondary_contact_email}</p>
                          </div>
                        </div>
                      </div>
                    ) : household.pending_invite ? (
                      <div className="bg-gold/10 rounded-lg p-4 border border-gold/30">
                        <div className="flex items-start justify-between mb-2">
                          <Badge className="bg-gold text-navy text-[10px]">Pending Invite</Badge>
                          <button 
                            onClick={handleCancelInvite}
                            className="text-red hover:text-red/80 text-xs flex items-center"
                            data-testid="cancel-invite-btn"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancel
                          </button>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-gold/30 flex items-center justify-center text-navy font-bold">
                            <Send className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-navy">{household.pending_invite.name || 'Invitation Sent'}</p>
                            <p className="text-xs text-gray-500">{household.pending_invite.email}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button 
                        onClick={() => setShowInviteParent(true)}
                        className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-navy hover:bg-navy/5 transition-all group"
                        data-testid="invite-parent-btn"
                      >
                        <div className="flex items-center justify-center space-x-3 py-2">
                          <div className="w-10 h-10 rounded-full bg-gray-200 group-hover:bg-navy/20 flex items-center justify-center transition-colors">
                            <UserPlus className="w-5 h-5 text-gray-500 group-hover:text-navy" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-gray-600 group-hover:text-navy">Add Second Parent</p>
                            <p className="text-xs text-gray-400">Invite another guardian</p>
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Contact & Address Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                      <div className="flex items-center mt-1">
                        <Phone className="w-4 h-4 text-gray-400 mr-2" />
                        <p className="text-gray-700">{household.phone || 'Not provided'}</p>
                      </div>
                    </div>
                    {/* SMS opt-in (Twilio compliance, Feb 2026):
                        - Default-deny, unchecked. User must actively opt in.
                        - Names the sender (Park City Chiefs) in the disclosure.
                        - Includes message type, frequency, HELP/STOP, opt-in-not-required.
                        - Masks the phone number, showing only the last 4 digits.
                        - Terms + Privacy Policy links sit directly beneath the checkbox. */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid="sms-optin-card">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 text-navy focus:ring-navy"
                          checked={!!smsPrefs.sms_enabled}
                          disabled={smsSaving}
                          onChange={(e) => handleSmsToggle(e.target.checked)}
                          data-testid="sms-optin-checkbox"
                        />
                        <div className="text-xs text-gray-700 leading-snug">
                          <p className="font-semibold text-navy mb-1">SMS alerts (optional)</p>
                          <p>
                            I agree to receive recurring informational text messages from{' '}
                            <strong>Park City Chiefs</strong> about practices, games, events,
                            schedule changes, cancellations, volunteer opportunities, and
                            organization updates. Message frequency varies. Message and data
                            rates may apply. Reply <strong>HELP</strong> for help or{' '}
                            <strong>STOP</strong> to opt out. Consent is optional and is not a
                            condition of registration.
                          </p>
                          {smsPrefs.phone_e164 ? (
                            <p className="mt-1 text-[11px] text-gray-500" data-testid="sms-optin-phone">
                              Texts will be sent to: (***) ***-{String(smsPrefs.phone_e164).slice(-4)}
                            </p>
                          ) : (
                            <p className="mt-1 text-[11px] text-red-700" data-testid="sms-optin-no-phone">
                              Add a phone number to your profile before enabling SMS alerts.
                            </p>
                          )}
                          {smsError && (
                            <p className="mt-1 text-[11px] text-red-700" data-testid="sms-optin-error">{smsError}</p>
                          )}
                        </div>
                      </label>
                      <p className="mt-2 pl-7 text-[11px] text-gray-500">
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-navy underline hover:no-underline"
                          data-testid="sms-optin-terms-link"
                        >
                          Terms of Service
                        </a>
                        {' · '}
                        <a
                          href="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-navy underline hover:no-underline"
                          data-testid="sms-optin-privacy-link"
                        >
                          Privacy Policy
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Emergency Contact</p>
                      <div className="flex items-center mt-1">
                        <Phone className="w-4 h-4 text-gray-400 mr-2" />
                        <p className="text-gray-700">{household.emergency_contact || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Address</p>
                      <div className="flex items-start mt-1">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                        <div>
                          {household.address?.street ? (
                            <>
                              <p className="text-gray-700">{household.address.street}</p>
                              <p className="text-gray-700">
                                {household.address.city}, {household.address.state} {household.address.zip}
                              </p>
                            </>
                          ) : (
                            <p className="text-gray-500">No address on file</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <InfoBanner 
                title="Complete your household profile"
                description="Add your address and emergency contact information to finish setting up your account."
              />
            )}
          </div>
        </div>

        {/* Invite Parent Modal */}
        {showInviteParent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-navy uppercase tracking-tight flex items-center">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Add Second Parent/Guardian
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowInviteParent(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-sm text-gray-600">
                  Invite another parent or guardian to join your household. They'll be able to manage players and view all family information.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name (optional)</label>
                  <Input
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Parent's name"
                    data-testid="invite-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="parent@email.com"
                    data-testid="invite-email-input"
                  />
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> If this email is already registered, they'll be added immediately. 
                    Otherwise, they'll receive an invitation to create an account.
                  </p>
                </div>
                
                <div className="flex space-x-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowInviteParent(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-navy hover:bg-navy/90"
                    onClick={handleInviteParent}
                    disabled={inviteLoading || !inviteForm.email}
                    data-testid="send-invite-btn"
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Teacher Modal */}
        {showAddTeacher && addingTeacherForPlayer && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-navy uppercase tracking-tight flex items-center">
                  <GraduationCap className="w-5 h-5 mr-2" />
                  Add Teacher for {addingTeacherForPlayer.first_name}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddTeacher(false); setAddingTeacherForPlayer(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-sm text-gray-600">
                  Add your child's teachers so they can provide weekly academic and behavior feedback.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teacher's Name *</label>
                  <Input
                    value={teacherForm.name}
                    onChange={(e) => setTeacherForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Mrs. Johnson"
                    data-testid="teacher-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teacher's Email *</label>
                  <Input
                    type="email"
                    value={teacherForm.email}
                    onChange={(e) => setTeacherForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="teacher@school.edu"
                    data-testid="teacher-email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School (optional)</label>
                  <Input
                    value={teacherForm.school}
                    onChange={(e) => setTeacherForm(prev => ({ ...prev, school: e.target.value }))}
                    placeholder="e.g., Park City Elementary"
                    data-testid="teacher-school-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject/Class (optional)</label>
                  <Input
                    value={teacherForm.subject}
                    onChange={(e) => setTeacherForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="e.g., Math, Homeroom"
                    data-testid="teacher-subject-input"
                  />
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>How it works:</strong> Each week, your child's teachers will receive an email 
                    with a simple link to provide a quick academic and behavior check-in. No login required for teachers.
                  </p>
                </div>
                
                <div className="flex space-x-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setShowAddTeacher(false); setAddingTeacherForPlayer(null); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-navy hover:bg-navy/90"
                    onClick={handleAddTeacher}
                    disabled={teacherLoading || !teacherForm.name || !teacherForm.email}
                    data-testid="save-teacher-btn"
                  >
                    {teacherLoading ? 'Adding...' : 'Add Teacher'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit School Modal */}
        {showEditSchool && editingSchoolPlayer && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-navy uppercase tracking-tight flex items-center">
                  <School className="w-5 h-5 mr-2" />
                  Edit School for {editingSchoolPlayer.first_name}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowEditSchool(false); setEditingSchoolPlayer(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-sm text-gray-600">
                  Update your child's school and grade information for academic tracking.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                  <Input
                    value={editSchoolForm.school}
                    onChange={(e) => setEditSchoolForm(prev => ({ ...prev, school: e.target.value }))}
                    placeholder="e.g., Park City Elementary"
                    data-testid="edit-school-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                  <select
                    value={editSchoolForm.grade}
                    onChange={(e) => setEditSchoolForm(prev => ({ ...prev, grade: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-navy focus:border-navy"
                    data-testid="edit-grade-select"
                  >
                    <option value="">Select Grade</option>
                    <option value="Pre-K">Pre-K</option>
                    <option value="K">Kindergarten</option>
                    <option value="1">1st Grade</option>
                    <option value="2">2nd Grade</option>
                    <option value="3">3rd Grade</option>
                    <option value="4">4th Grade</option>
                    <option value="5">5th Grade</option>
                    <option value="6">6th Grade</option>
                    <option value="7">7th Grade</option>
                    <option value="8">8th Grade</option>
                    <option value="9">9th Grade</option>
                    <option value="10">10th Grade</option>
                    <option value="11">11th Grade</option>
                    <option value="12">12th Grade</option>
                  </select>
                </div>
                
                <div className="flex space-x-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setShowEditSchool(false); setEditingSchoolPlayer(null); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-navy hover:bg-navy/90"
                    onClick={handleSaveSchool}
                    disabled={schoolLoading}
                    data-testid="save-school-btn"
                  >
                    {schoolLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Finance F4 — Household Payments Snapshot. Read-only summary
            over `players` + `registrations` already in state. Surfaces
            balance due, amount paid, payment status, and receipt number
            (when available) so parents can see the whole household's
            payment picture in one place. No new API call. */}
        {(() => {
          const rows = players
            .map((p) => ({ player: p, reg: registrations[p.player_id] }))
            .filter((r) => r.reg);

          if (rows.length === 0) return null;

          const totalPaid = rows.reduce(
            (acc, r) => acc + Number(r.reg.amount_paid || 0),
            0
          );
          const totalOutstanding = rows.reduce((acc, r) => {
            const paid = Number(r.reg.amount_paid || 0);
            const total = Number(r.reg.total_amount || 0);
            const status = r.reg.payment_status;
            if (status === 'waived') return acc;
            return acc + Math.max(0, total - paid);
          }, 0);

          const statusMeta = (status) => {
            if (status === 'confirmed' || status === 'paid')
              return { label: 'Confirmed', cls: 'bg-green-100 text-green-700' };
            if (status === 'waived')
              return { label: 'Waived', cls: 'bg-sky-100 text-sky-700' };
            if (status === 'submitted')
              return { label: 'Awaiting Confirmation', cls: 'bg-blue-100 text-blue-700' };
            if (status === 'pending')
              return { label: 'Pending', cls: 'bg-amber-100 text-amber-700' };
            if (status === 'failed' || status === 'rejected')
              return { label: 'Rejected', cls: 'bg-rose-100 text-rose-700' };
            return { label: 'Not Started', cls: 'bg-gray-100 text-gray-600' };
          };

          return (
            <div
              className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6"
              data-testid="household-payments-card"
            >
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payments
                  </h2>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                    <span data-testid="household-payments-total-paid">
                      Total paid:{' '}
                      <span className="font-semibold text-navy">
                        ${totalPaid.toFixed(2)}
                      </span>
                    </span>
                    <span data-testid="household-payments-total-outstanding">
                      Balance due:{' '}
                      <span
                        className={`font-semibold ${
                          totalOutstanding > 0 ? 'text-red' : 'text-navy'
                        }`}
                      >
                        ${totalOutstanding.toFixed(2)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-3">
                {rows.map(({ player, reg }) => {
                  const meta = statusMeta(reg.payment_status);
                  const paid = Number(reg.amount_paid || 0);
                  const total = Number(reg.total_amount || 0);
                  const balance =
                    reg.payment_status === 'waived'
                      ? 0
                      : Math.max(0, total - paid);
                  const program =
                    reg.registration_data?.program_type?.program_type
                      ?.replace('_', ' ');
                  // Paid-state polish: when the registration is paid/
                  // confirmed and we have a confirmation timestamp, swap
                  // the generic "Confirmed" badge for a date-stamped
                  // "Paid {date}" label and inline the receipt number
                  // (when present) right beside it. Gives parents instant
                  // confidence the payment landed.
                  const isPaidLike =
                    reg.payment_status === 'paid' ||
                    reg.payment_status === 'confirmed';
                  const paidAtRaw =
                    reg.payment_confirmed_at ||
                    reg.updated_at ||
                    null;
                  let paidLabel = null;
                  if (isPaidLike && paidAtRaw) {
                    try {
                      paidLabel = `Paid ${format(new Date(paidAtRaw), 'MMM d, yyyy')}`;
                    } catch {
                      paidLabel = 'Paid';
                    }
                  }
                  return (
                    <div
                      key={reg.registration_id}
                      className="border border-gray-200 rounded-lg p-3 sm:p-4"
                      data-testid={`household-payment-row-${reg.registration_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-navy truncate">
                              {player.first_name} {player.last_name}
                            </p>
                            <Badge
                              className={`${meta.cls} text-[11px]`}
                              data-testid={`household-status-badge-${reg.registration_id}`}
                            >
                              {paidLabel || meta.label}
                            </Badge>
                            {paidLabel && reg.receipt_number && (
                              <span
                                className="text-[11px] text-gray-500"
                                data-testid={`household-receipt-inline-${reg.registration_id}`}
                              >
                                · Receipt #
                                <span className="font-mono text-gray-700 ml-0.5">
                                  {reg.receipt_number}
                                </span>
                              </span>
                            )}
                          </div>
                          {program && (
                            <p className="text-xs text-gray-500 capitalize mt-0.5">
                              {program}
                            </p>
                          )}
                          {!paidLabel && reg.receipt_number && (
                            <p
                              className="text-[11px] text-gray-500 mt-1"
                              data-testid={`household-receipt-${reg.registration_id}`}
                            >
                              Receipt #
                              <span className="font-mono text-gray-700 ml-1">
                                {reg.receipt_number}
                              </span>
                              <span className="ml-2 text-gray-400">
                                (also emailed to you)
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm">
                            <span className="text-gray-500">Paid:</span>{' '}
                            <span className="font-semibold text-navy">
                              ${paid.toFixed(2)}
                            </span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className="text-gray-600">
                              ${total.toFixed(2)}
                            </span>
                          </p>
                          {balance > 0 && (
                            <p className="text-xs text-red mt-0.5">
                              ${balance.toFixed(2)} due
                            </p>
                          )}
                          {balance > 0 && reg.payment_status !== 'submitted' && (
                            <Button
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/registration/${reg.registration_id}?step=payment`
                                )
                              }
                              className="mt-2 bg-red hover:bg-red/90 text-white"
                              data-testid={`household-pay-balance-${reg.registration_id}`}
                            >
                              Pay balance
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Players Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="players-card">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight flex items-center">
                <Users className="w-5 h-5 mr-2" />
                My Children
                <span className="ml-2 text-sm font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{totalChildren}</span>
              </h2>
              <Button 
                className="bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider text-xs" 
                size="sm"
                onClick={() => setShowAddPlayer(true)}
                data-testid="add-player-trigger"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Add Player</span>
              </Button>
            </div>
          </div>
          <div className="p-5">
            {players.length > 0 ? (
              <div className="space-y-4">
                {players.map((player) => {
                  const EligibilityIcon = getEligibilityIcon(player.eligibility_status);
                  return (
                    <div
                      key={player.player_id}
                      className="border border-gray-200 rounded-xl p-4 hover:border-navy/30 hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => openPlayerDetail(player)}
                      data-testid={`player-${player.player_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        {/* Player Avatar & Basic Info */}
                        <div className="flex items-center space-x-4 flex-1">
                          <PlayerAvatar
                            photoUrl={player.photo_url}
                            firstName={player.first_name}
                            lastName={player.last_name}
                            size="lg"
                            className="flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-semibold text-navy text-lg truncate">
                                {player.first_name} {player.last_name}
                              </h3>
                              {player.jersey_number && (
                                <Badge variant="secondary" className="bg-gold/20 text-navy font-bold">
                                  #{player.jersey_number}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {getDivisionLogo(player.age_group) && (
                                <img 
                                  src={getDivisionLogo(player.age_group)} 
                                  alt="" 
                                  className="w-8 h-8 object-contain"
                                />
                              )}
                              <span className="text-sm text-gray-600">{getDivisionDisplay(player.age_group)}</span>
                              {player.position && (
                                <>
                                  <span className="text-gray-400">|</span>
                                  <span className="text-sm text-gray-600">{player.position}</span>
                                </>
                              )}
                              {player.team_name && (
                                <>
                                  <span className="text-gray-400">|</span>
                                  <span className="text-sm font-medium text-navy">{player.team_name}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status & Actions */}
                        <div className="flex items-center justify-between sm:justify-end space-x-3">
                          <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border ${getEligibilityColor(player.eligibility_status)}`}>
                            <EligibilityIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {formatEligibilityLabel(player.eligibility_status)}
                            </span>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>

                      {/* Player Details Row */}
                      <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Date of Birth</p>
                          <p className="font-medium text-navy">
                            {formatLocalDOB(player.date_of_birth)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Grade</p>
                          <p className="font-medium text-navy">{player.grade || 'Not set'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">School</p>
                          <p className="font-medium text-navy truncate">{player.school || 'Not set'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Registration</p>
                          <div 
                            className="cursor-pointer hover:opacity-80"
                            onClick={(e) => {
                              e.stopPropagation();
                              const playerReg = registrations[player.player_id];
                              if (playerReg?.registration_id) {
                                navigate(`/registration/${playerReg.registration_id}`);
                              }
                            }}
                          >
                            {(() => {
                              // Feb 2026 P0 sync fix: prefer the live registration
                              // state over stale player.registration_status. A player
                              // with an approved+paid registration is operationally
                              // "Registered" regardless of what player.registration_status
                              // was set to at player-creation time.
                              const playerReg = registrations[player.player_id];
                              const paidOk = playerReg && (
                                ['paid', 'confirmed', 'waived'].includes(playerReg.payment_status) ||
                                Number(playerReg.total_amount || 0) === 0
                              );
                              let effective;
                              if (playerReg?.status === 'approved' && paidOk) {
                                effective = 'registered';
                              } else if (playerReg?.status) {
                                effective = playerReg.status;
                              } else {
                                effective = player.registration_status || 'incomplete';
                              }
                              return <StatusBadge status={effective} size="sm" />;
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Action Required Banner - Show for incomplete registrations */}
                      {(() => {
                        const playerReg = registrations[player.player_id];
                        // Feb 2026 P0 sync fix: derive needsAction from the LIVE
                        // registration record, not from stale player.eligibility_status
                        // which is set once at player creation and never updated.
                        //
                        // Aug 2026 follow-up: also honor player.registration_status
                        // === "registered" as an authoritative "no action needed"
                        // signal. When the backend forward-sync helper has already
                        // rolled up a paid-and-approved state onto the player
                        // document, the parent Family page may still receive
                        // playerReg=undefined (e.g. household-scoping edge cases).
                        // In that case the badge already falls back to
                        // player.registration_status; the banner gate must do the
                        // same or Start Registration will incorrectly render for
                        // an operationally Registered player.
                        const paidOk = playerReg && (
                          ['paid', 'confirmed', 'waived'].includes(playerReg.payment_status) ||
                          Number(playerReg.total_amount || 0) === 0
                        );
                        const isRegistered = (playerReg?.status === 'approved' && paidOk)
                          || player.registration_status === 'registered';
                        const needsAction = !isRegistered && (
                          !playerReg ||
                          ['not_started', 'in_progress', 'incomplete', 'pending_review', 'submitted'].includes(playerReg?.status) ||
                          (playerReg?.status === 'approved' && !paidOk)
                        );

                        if (isRegistered || !needsAction) return null;
                        
                        let message = 'Complete registration to continue';
                        let buttonText = 'Continue';
                        
                        if (!playerReg || playerReg.status === 'not_started') {
                          message = 'Start registration to participate this season';
                          buttonText = 'Start Registration';
                        } else if (playerReg?.status === 'in_progress') {
                          message = 'Continue where you left off';
                          buttonText = 'Continue Registration';
                        } else if (player.eligibility_status === 'pending_payment') {
                          message = 'Payment required to complete registration';
                          buttonText = 'Complete Payment';
                        } else if (player.eligibility_status === 'pending_docs') {
                          message = 'Documents needed for clearance';
                          buttonText = 'Upload Documents';
                        } else if (playerReg?.status === 'incomplete') {
                          message = 'Registration incomplete - please review and complete';
                          buttonText = 'Complete Registration';
                        }
                        
                        return (
                          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <AlertCircle className="w-4 h-4 text-yellow-600" />
                              <span className="text-sm text-yellow-800">{message}</span>
                            </div>
                            <Button 
                              size="sm" 
                              className="bg-yellow-600 hover:bg-yellow-700 text-white"
                              onClick={async (e) => {
                                e.stopPropagation();
                                // If registration exists, navigate to it
                                if (playerReg?.registration_id) {
                                  navigate(`/registration/${playerReg.registration_id}`);
                                } else {
                                  // Start a new registration
                                  try {
                                    const response = await axios.post(
                                      `${API_URL}/api/registrations/start?player_id=${player.player_id}&season_id=fall_2026`,
                                      {},
                                      { withCredentials: true }
                                    );
                                    if (response.data?.registration_id) {
                                      navigate(`/registration/${response.data.registration_id}`);
                                    }
                                  } catch (err) {
                                    console.error('Failed to start registration:', err);
                                    alert('Failed to start registration. Please try again.');
                                  }
                                }
                              }}
                            >
                              {buttonText}
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                icon={Users}
                title="No players registered"
                description="Add your children to get started with registration for the upcoming season."
                actionLabel="Add Your First Player"
                action={() => setShowAddPlayer(true)}
              />
            )}
          </div>
        </div>

        {/* Quick Links - Athletic Style */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/schedule')}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 text-left hover:-translate-y-1 hover:shadow-lg hover:border-navy/30 transition-all duration-200"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center mr-3">
                <Calendar className="w-5 h-5 text-navy" />
              </div>
              <div>
                <p className="font-bold text-navy">View Schedule</p>
                <p className="text-xs text-gray-500 font-body">See upcoming practices & games</p>
              </div>
            </div>
          </button>
          <button 
            onClick={() => navigate('/communications')}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 text-left hover:-translate-y-1 hover:shadow-lg hover:border-light-blue/30 transition-all duration-200"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-light-blue/10 rounded-lg flex items-center justify-center mr-3">
                <Mail className="w-5 h-5 text-light-blue" />
              </div>
              <div>
                <p className="font-bold text-navy">Messages</p>
                <p className="text-xs text-gray-500 font-body">Check announcements & updates</p>
              </div>
            </div>
          </button>
          <button 
            onClick={() => navigate('/volunteer')}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 text-left hover:-translate-y-1 hover:shadow-lg hover:border-gold/30 transition-all duration-200"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center mr-3">
                <Shield className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="font-bold text-navy">Volunteer</p>
                <p className="text-xs text-gray-500 font-body">Sign up to help the team</p>
              </div>
            </div>
          </button>
        </div>
      </div>
      
      {/* Modals - rendered as functions to avoid React re-creating components on each render */}
      {renderEditHouseholdModal()}
      {renderAddPlayerModal()}
      {renderEditPlayerModal()}
      {renderPlayerDetailModal()}
      {renderDeleteConfirmModal()}
    </ParentLayout>
  );
};

export default FamilyPage;
